import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IAccessoryCategory, IAccessoryType } from '../interfaces/heavyDutyMachinery/IAccessoryTaxonomy';
import { getAccessoryTaxonomy, findAccessoryTypeByCode } from '../services/AccessoryTaxonomyService';

/**
 * Fetches the AccessoryCategory/AccessoryFamily/AccessoryType taxonomy for
 * the current i18next language and resolves a
 * MachineryAccessory.AccessoryTypeCode value to its already-localized
 * display name -- see useTaxonomy's own doc comment for the identical
 * reasoning (shared per-language cache, re-fetch on language change,
 * `categories` null while loading, `resolveName` falls back to the raw code).
 *
 * `enabled` (default true) skips the fetch entirely -- for a caller like
 * HomeSearchBar that renders in every mode but only needs this taxonomy in
 * 'accessories' mode, so the other modes don't trigger a needless request.
 */
export const useAccessoryTaxonomy = (enabled: boolean = true) => {
  const { i18n } = useTranslation();
  const [categories, setCategories] = useState<IAccessoryCategory[] | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setCategories(null);
    getAccessoryTaxonomy(i18n.language)
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, i18n.language]);

  const resolveName = (code?: string | null): string => {
    if (!code) return '';
    const type: IAccessoryType | undefined = categories ? findAccessoryTypeByCode(categories, code) : undefined;
    return type ? type.name : code;
  };

  return { categories, loading: categories === null, resolveName };
};
